import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { rendersAsPdf } from '@shared';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { MarkdownModule } from 'ngx-markdown';
import * as fromGenerated from '../../../../_generated';
import { ResponseChartComponent } from '../../../../charts/response-chart.component';
import * as fromFeatureFlags from '../../../../feature-flags';
import { DocumentInChatMessage } from '../../conversation.page';
import {
  excludeImageDocuments,
  isImageDocumentExtension,
} from './message-documents.helpers';
import { MessageQuickActionsSmartComponent } from './quick-actions/message-quick-actions.smart.component';
import { ResponseTableCopyComponent } from './response-table/response-table-copy.component';
import { ResponseTableSaveComponent } from './response-table/response-table-save.component';

gsap.registerPlugin(SplitText);

export interface FileClickedEvent {
  documentUuid: string;
}

export interface ImageInMessageClickedEvent {
  documentUuid: string;
  filename: string;
}

export type MessageDocuments = Pick<
  fromGenerated.DocumentView,
  'uuid' | 'filename' | 'extension' | 'deletedAt' | 'status'
> & {
  isLoading?: boolean;
  isError?: boolean;
  isDisabled?: boolean;
  isClickable?: boolean; // by default, all documents are clickable
  type?: 'file' | 'document';
};

/**
 * The widget's default bottom spacing, as a Tailwind class.
 *
 * A named constant rather than a literal inside `tableBottomSpacing` so the
 * string Tailwind has to see at build time sits at the top of the file next to
 * the only other place it is written. It is generated because this `.ts` is in
 * the content globs, which is also why it must stay a whole literal — a class
 * assembled from fragments at runtime would not be generated at all.
 */
const BELOW_TABLE = 'tablet-landscape:mb-2';

/**
 * A CommonMark thematic break: up to three spaces of indent, then three or more
 * of `*`, `-` or `_` — all the same character — separated and followed by
 * nothing but spaces and tabs.
 *
 * Only the segment's first non-blank line is ever tested, which is what keeps
 * this away from the trap KD-6 records: a hand-rolled matcher goes wrong on a
 * fenced code block containing table or rule syntax, and that content is always
 * on a later line than the fence that opens it. Segmenting is still marked's
 * job; this only asks what an already-segmented chunk starts with.
 *
 * ⚠️ Being wrong here costs 8px of spacing and nothing else — a missed break
 * leaves the divider at 36px, a false positive pulls the next segment 8px
 * closer. That is the reason a regex is proportionate where the segmenter
 * itself needed a real lexer.
 */
const THEMATIC_BREAK_RE = /^ {0,3}(?:([*\-_])[ \t]*)(?:\1[ \t]*){2,}$/;

/**
 * The same break written as raw HTML — `<hr>`, `<hr/>`, `<hr class="x">`.
 *
 * `marked` lexes a raw `<hr>` into its own `hr` token, so it opens a prose
 * segment exactly as `---` does and produces the same divider. Without this the
 * bug report's own repro — *"write a table followed by an hr tag then a p tag"* —
 * took the wrong arm and sat 8px wider than the 28px contract SB-3522
 * established, which is the one case the fix was most likely to be looked at in.
 *
 * The `\b` after `hr` is what keeps a tag like `<href>` out.
 */
const HTML_THEMATIC_BREAK_RE = /^ {0,3}<hr\b[^>]*>[ \t]*$/i;

const opensWithThematicBreak = (
  segment: fromSeriousSystem.SdResponseSegment | undefined,
): boolean => {
  if (segment?.kind !== 'markdown') {
    return false;
  }
  const firstLine = segment.content
    .split('\n')
    .find((line) => line.trim() !== '');

  return (
    firstLine !== undefined &&
    (THEMATIC_BREAK_RE.test(firstLine) ||
      HTML_THEMATIC_BREAK_RE.test(firstLine))
  );
};

@Component({
  selector: 'squadbox-messages-listing-message',
  imports: [
    MarkdownModule,
    fromSeriousSystem.FileChipComponent,
    fromSeriousSystem.ResponseTableComponent,
    fromFeatureFlags.FeatureFlagsDirective,
    MessageQuickActionsSmartComponent,
    // Referenced only inside the chart branch's @defer, so it is lazy.
    ResponseChartComponent,
    ResponseTableCopyComponent,
    ResponseTableSaveComponent,
    TranslatePipe,
  ],
  template: `
    <!--                 -->
    <!-- Loading Message -->
    <!--                 -->
    @if (isLoading()) {
      <div
        class="rounded-2xl typo-p2 p-2 prose bg-primary-200/20 animate-pulse text-transparent"
      >
        {{ messageContent() }}
      </div>
      <!--              -->
      <!-- User Message -->
      <!--              -->
    } @else if (isFromUser()) {
      <div class="fade-in flex flex-col items-end">
        <div
          class="px-4 py-3 max-w-70 rounded-2xl typo-p2 p-2 bg-primary-500/10"
          [class.tablet-landscape:max-w-110]="
            isFromUser() && !selectedDocumentInChatMessage()?.documentUuid
          "
          [class.hidden]="!messageContent()"
        >
          <markdown [data]="messageContent()" />
        </div>
        <div
          [id]="'scroll-anchor-' + messageId()"
          style="height: 0; scroll-margin-top: 152px"
        ></div>
        @let messageDocs = messageDocuments();
        @if (messageDocs.length > 0) {
          <!-- User Message Documents -->
          <div class="flex flex-wrap place-content-end pt-1 gap-1">
            @for (document of messageDocs; track document.uuid) {
              <sd-file-chip
                [filename]="document.filename"
                [fileType]="getFileTypeIconFromExtension(document.extension)"
                [isSelected]="
                  selectedDocumentInChatMessage()?.documentUuid ===
                    document.uuid &&
                  selectedDocumentInChatMessage()?.chatMessageId === messageId()
                "
                [isDisabled]="
                  !!document.deletedAt || (document.isDisabled ?? false)
                "
                [isClickable]="document.isClickable ?? isChipOpenable(document)"
                [isLoading]="document.isLoading ?? false"
                [isError]="
                  document.isError || document.status === 'failed' || false
                "
                (chipClicked)="handleFileChipClicked(document)"
              />
            }
          </div>
        }
      </div>
      <!--                 -->
      <!-- Chatbot Message -->
      <!--                 -->
    } @else if (!isFromUser()) {
      <div class="flex gap-1 tablet-landscape:gap-3">
        <!-- Chatbot Message Content -->
        <!-- w-full, not max-w-full (SB-3236): as a row flex item that does
             not grow, the column shrank to its longest line, so a divider
             in a short reply stopped there instead of spanning the chat.
             The live turn's answer already spans it, so the handoff to the
             saved reply no longer narrows anything. -->
        <div class="flex flex-col gap-2 typo-p2 p-2 w-full">
          <!--                                                              -->
          <!-- Segment wrapper — the GSAP animation root (KD-9)             -->
          <!--                                                              -->
          <!-- One markdown element per prose segment, one widget per table
               segment, in source order. The wrapper is deliberately a NEW
               inner element rather than the flex column above it, which is
               what keeps the quick-actions row and the file-chip row OUT of
               blockSelector's scope — they stay siblings of the wrapper and
               keep their own reveal via their own refs, exactly as before
               segmentation. Re-rooting the reveal onto a wrapper risks pulling
               those rows into its scope (KD-9); it does not, by construction.

               The initial-hide lives HERE and nowhere else. GSAP's autoAlpha
               writes an inline opacity onto the root, so an opacity-0 class
               left on each markdown element would never be overridden and
               prose would stay invisible for good — not for a frame. It also
               settles whether the widget needs an initial-hide of its own
               (KD-9): it does not — it sits inside the hidden subtree and
               cannot flash. The fade-in class stays per segment because it is the
               non-animated path's own transition (styles.css:440), unrelated
               to the reveal. -->
          <div
            #segmentWrapper
            class="flex flex-col gap-2"
            [class.opacity-0]="isNewReply()"
          >
            @for (segment of segments(); track $index) {
              @if (segment.kind === 'table') {
                <!-- wrap-normal is defensive only (KD-7): the widget is a
                     SIBLING of the markdown element, so it never inherits
                     the global wrap-anywhere. The real no-break requirement
                     is the widget's own break-normal cells. -->
                <!-- 8px more above AND below the widget on desktop, on top of
                     the wrapper's gap, for 16px on each side. Figma gives an
                     embedded block more room than a paragraph gets: the desktop
                     Table wrapper is 338 tall around a 322 Content at y=16, and
                     the Call-to-Action and Buttons wrappers beside it use the
                     same 16. Mobile keeps 8 (its Table wrapper is 330 around
                     the same 322 at y=8), so the extra is breakpoint-scoped
                     rather than unconditional.

                     The bottom half is SB-3522 design validation, and the cause
                     is segmentation rather than the gap being wrong. Prose after
                     a table lands in its own markdown element, where the
                     first-child pt-0 reset in styles.css strips the top padding
                     that block carried when it was mid-element — 16px for a
                     heading, 8px for a paragraph. So an H3 after a table read
                     tighter than two plain paragraphs do.

                     ⚠️ 16 is a mirror of the validated spacing above, NOT a
                     measurement: Figma has no prose-after-table frame to take
                     one from — its Table is the last block before the Action
                     Bar in all five Desktop - Home frames.

                     The bottom value is conditional — see tableBottomSpacing,
                     which owns the two exceptions design asked for.

                     Deliberately NOT done by widening the wrapper's gap: that
                     gap is shared by every segment in every reply, so changing
                     it would re-space prose-to-prose everywhere and move a
                     rhythm the no-table baseline pins. -->
                <sd-response-table
                  class="wrap-normal tablet-landscape:mt-2"
                  [class]="tableBottomSpacing($index)"
                  [class.fade-in]="!isNewReply() && !wasStreamed()"
                  [table]="segment.table"
                >
                  <!-- Both actions project into the widget's title-bar slot;
                       neither could place itself, so this is the only place
                       they render in the chat.

                       Nothing is bound to them, and that is not an omission:
                       neither component exposes an output. Each injects
                       AnalyticsService and POSTs its own table_copied /
                       table_downloaded, which is why an event handler here
                       would not compile rather than merely duplicate. Adding
                       one back means adding the output back first, and it was
                       removed deliberately — a component with no
                       cross-boundary consumer does not need one. -->
                  <squadbox-response-table-save [table]="segment.table" />
                  <squadbox-response-table-copy [table]="segment.table" />
                </sd-response-table>
              } @else if (segment.kind === 'chart') {
                <!-- Deferred, so the validator and the chart
                     component stay out of the initial bundle.
                     @error covers only this chunk failing to load: nothing
                     can read the block without the validator, so it shows
                     the notice. The renderer chunk failing (6.11) is the
                     chart component's own table fallback. -->
                <!-- A reply the user just watched stream already drew this
                     chart live (SB-3651): it loads at once, without a fade or
                     a second entrance, so the handoff changes nothing. -->
                @defer (on viewport; when wasStreamed()) {
                  <squadbox-response-chart
                    class="wrap-normal tablet-landscape:my-2"
                    [class.fade-in]="!isNewReply() && !wasStreamed()"
                    [source]="segment.source"
                    [entrance]="!wasStreamed()"
                  />
                } @placeholder {
                  <div class="min-h-20"></div>
                } @error {
                  <p role="status" class="typo-p2">
                    {{ 'CHAT.CHART.UNAVAILABLE' | translate }}
                  </p>
                }
              } @else {
                <markdown
                  [class.fade-in]="!isNewReply() && !wasStreamed()"
                  [data]="segment.content"
                />
              }
            }
          </div>
          <!-- Chatbot Message Quick Actions -->
          @let selectedChatUuid = chatUuid();
          @let selectedChatNumericId = chatId();
          @if (
            !isAssistantDescriptionOrIntroMessage() &&
            !isCompliancePhaseRegular() &&
            isQuickActionsDisplayed() &&
            ffQuickActions().hasAnyTrueValue &&
            selectedChatUuid &&
            selectedChatNumericId
          ) {
            <!-- A streamed reply's actions rise in once its last word is on
                 screen (SB-3651, the simulation's action bar); others fade. -->
            <squadbox-message-quick-actions-smart
              class="py-1"
              [class.fade-in]="!wasStreamed()"
              [class.reply-enter]="wasStreamed()"
              #chatbotQuickActions
              [chatUuid]="selectedChatUuid"
              [chatId]="selectedChatNumericId"
              [messageContent]="messageContent()"
              [messageId]="messageId()"
              [isProcessing]="isProcessing()"
              [isLastResponse]="isLastResponse()"
            />
          }
          @let chatbotReplyDocs = chatbotReplyDocuments();
          @if (chatbotReplyDocs.length > 0) {
            <div
              #chatbotDocumentsContainer
              [squadboxFeatureFlag]="'ff-display-document-sources'"
              class="flex flex-wrap pt-0 gap-2"
              [class.fade-in]="!wasStreamed()"
              [class.reply-enter]="wasStreamed()"
            >
              @for (document of chatbotReplyDocs; track document.uuid) {
                <sd-file-chip
                  [filename]="document.filename"
                  [fileType]="getFileTypeIconFromExtension(document.extension)"
                  [isLoading]="false"
                  [isDisabled]="!!document.deletedAt"
                  [isClickable]="
                    document.isClickable ?? isChipOpenable(document)
                  "
                  [isSelected]="
                    selectedDocumentInChatMessage()?.documentUuid ===
                      document.uuid &&
                    selectedDocumentInChatMessage()?.chatMessageId ===
                      messageId()
                  "
                  (chipClicked)="handleFileChipClicked(document)"
                />
              }
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class MessagesListingMessageComponent implements OnDestroy {
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);

  private markdownBlocksAnimation: gsap.core.Timeline | null = null;
  /**
   * The `SplitText` instances the current reveal built, so an interrupted pass
   * can put the prose back.
   *
   * Each split's own `revert()` is wired to the completion of its word tween,
   * which is exactly the thing that does not happen when a pass is cut short.
   * Left alone, the paragraph keeps the per-word `inline-block` spans
   * `SplitText` injected, and those change line breaking — so this is not
   * merely untidy DOM.
   */
  private markdownBlocksSplits: SplitText[] = [];
  /**
   * Handle for the pending `setTimeout` that builds the next reveal.
   *
   * The effect below re-runs on every `messageContent` change, so a streaming
   * reply used to queue one builder per chunk, each constructing a timeline
   * that re-hid every block. Only the last was ever tracked; the rest ran on as
   * orphans, mutating the same elements. Holding the handle lets a new pass
   * cancel the one it supersedes.
   */
  private markdownBlocksTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The blocks the current pass hid, so cleanup can put them back.
   *
   * They are hidden imperatively rather than through the timeline (see the
   * reasoning at the `gsap.set` that does it), which also puts them outside
   * what `revert()` knows how to undo — hence the explicit list.
   */
  private markdownBlocksHidden: HTMLElement[] = [];
  /**
   * The GSAP reveal root (KD-9), replacing the `viewChild<MarkdownComponent>`
   * this component used to key the reveal on.
   *
   * The old ref was not widened to a `viewChildren` over the now-N markdown
   * instances, even though that was the plan, because **nothing reads it any
   * more** — the reveal keys on the wrapper and no consumer outside this class
   * ever touched it. A `viewChildren` with no reader is worse than its absence.
   *
   * Keying on the wrapper is also the whole of KD-12's fix: the wrapper exists
   * for every chatbot message, including a table-only one, so the reveal runs
   * and `markdownAnimationComplete` emits where it previously could not —
   * `animateMarkdownBlocks` already emitted for zero blocks (see its own
   * empty-guard), so the bug was only ever the effect's entry guard.
   */
  public readonly segmentWrapper =
    viewChild<ElementRef<HTMLDivElement>>('segmentWrapper');
  public readonly chatbotQuickActions =
    viewChild<MessageQuickActionsSmartComponent>('chatbotQuickActions');
  public readonly chatbotDocumentsContainer = viewChild<
    ElementRef<HTMLDivElement>
  >('chatbotDocumentsContainer');

  public readonly chatUuid = input<string | null>(null);
  public readonly chatId = input<number | null>(null);
  public readonly messageId = input.required<number>();
  public readonly messageContent = input.required<string>();
  public readonly messageDocuments = input<MessageDocuments[]>([]);
  public readonly isFromUser = input.required<boolean>();
  public readonly isProcessing = input<boolean>(false);
  /**
   * The GSAP word reveal of a reply that just landed — the waiting UI with
   * `ff-model-thinking-streaming` off. With it on, the live turn writes the
   * reply out and hands over with `wasStreamed` instead.
   */
  public readonly isNewReply = input<boolean>(false);
  /**
   * SB-3651 — this message IS the reply the user just watched stream in. It
   * mounts fresh the instant the live turn hands off to the saved message, so
   * without this its segments would restart their 300ms `fade-in` on text
   * already on screen; its actions rise in instead.
   */
  public readonly wasStreamed = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);
  public readonly isMobile = input<boolean>(false);
  public readonly isQuickActionsDisplayed = input<boolean>(false);
  public readonly isLastResponse = input<boolean>(false);
  public readonly isAssistantDescriptionOrIntroMessage = input<boolean>(false);
  public readonly isCompliancePhaseRegular = input<boolean>(false);
  public readonly selectedDocumentInChatMessage =
    input<DocumentInChatMessage | null>(null);
  public readonly documentClicked = output<FileClickedEvent>();
  public readonly imageClicked = output<ImageInMessageClickedEvent>();
  public readonly markdownAnimationComplete = output<void>();
  public readonly spacerShouldRecalculate = output<void>();
  public readonly ffQuickActions = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectQuickActions,
  );

  /**
   * The message's prose and table segments, in source order (KD-6).
   *
   * Read by the chatbot branch only — a user message renders its content
   * verbatim through a single markdown element and is never segmented — so a
   * user message pays no lexing cost. A chatbot message pays it whether or not
   * it contains a table: that is new passive work on message arrival, bounded
   * per message by the parser's own cell budget but not aggregated across a
   * long conversation.
   */
  /**
   * ⚠️ Every `KD-n` in this file resolves against **SB-3514**'s Technical Plan,
   * `.ai/plans/SB-3514_table-rendering-in-chat/SB-3514-technical-plan.md`.
   *
   * Worth stating because the number alone is ambiguous in this very directory
   * tree: `KD-21` already means one thing in SB-3325's builder page and another
   * in SB-3474's upload store, and both of those qualify it with a ticket key.
   * The resolution note lives in `libs/serious-system/CLAUDE.md`, which does not
   * load for a reader working under `apps/hs/ui`.
   */
  public readonly segments = computed(() =>
    fromSeriousSystem.segmentResponse(this.messageContent()),
  );

  /**
   * The widget's bottom spacing, which depends on what comes after it (SB-3522,
   * second design-validation pass).
   *
   * The default is the 16px the template comment above explains. Design asked
   * for two exceptions, and each removes space rather than adding it:
   *
   * - **Last segment: nothing at all.** Figma agrees and is unusually explicit
   *   here — the desktop Table wrapper is 338 around a 322 Content at y=16, so
   *   its bottom inset is 0, and the Action Bar beneath supplies its own 8. The
   *   parent flex column's gap is that 8, so dropping the margin lands on
   *   Figma's number exactly instead of overshooting it by the widget's own.
   *
   * - **A divider next: pull back 8px.** `styles.css` gives `hr` a 28px
   *   `my-7` and then zeroes the padding on both of its neighbors, so a divider
   *   owns all the space around it and prose meets it at exactly 28px. A table
   *   cannot join that contract — those rules are scoped `markdown > *` and the
   *   widget is a sibling of the markdown elements, not a child of one — so it
   *   arrived at 44px. The negative margin cancels the inter-segment gap and
   *   puts it back on 28px, matching prose.
   *
   *   ⚠️ Unlike the positive margin this is **not** breakpoint-scoped, and that
   *   asymmetry is deliberate: the 16px above is a Figma value that differs
   *   between desktop and mobile, whereas the divider's 28px comes from
   *   `my-7` and is the same at every width. Scoping this to
   *   `tablet-landscape:` would leave mobile at 36px against prose's 28px.
   */
  protected tableBottomSpacing(index: number): string {
    const segments = this.segments();

    if (index === segments.length - 1) {
      return '';
    }
    return opensWithThematicBreak(segments[index + 1]) ? '-mb-2' : BELOW_TABLE;
  }

  constructor() {
    effect(() => {
      const wrapperEl = this.segmentWrapper();
      const content = this.messageContent();
      const fromUser = this.isFromUser();
      const isProcessing = this.isProcessing();
      const isNewReply = this.isNewReply();

      if (wrapperEl && !fromUser && content && isNewReply) {
        this.animateMarkdownBlocks(
          wrapperEl.nativeElement,
          () => this.markdownAnimationComplete.emit(),
          () => this.spacerShouldRecalculate.emit(),
        );
      } else if (!isProcessing) {
        this.cleanupResponseAnimation();
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupResponseAnimation();
  }

  public getFileTypeIconFromExtension(extension: string) {
    return fromSeriousSystem.getFileTypeIconFromExtension(extension);
  }

  /**
   * Documents rendered as chips UNDER a chatbot reply (the
   * `ff-display-document-sources` row) — images excluded (see
   * `excludeImageDocuments`). Scoped to the chatbot block only; the
   * user-message chip row keeps `messageDocuments()` so the sender's own
   * attached-image chip still renders and opens the overlay.
   */
  public readonly chatbotReplyDocuments = computed(() =>
    excludeImageDocuments(this.messageDocuments()),
  );

  /**
   * Whether a chip has any viewer to open at all: an image goes to the overlay,
   * a PDF-renderable document goes to the PDF viewer, and everything else
   * (audio, `md`/`yml`/`yaml`, anything unrecognized) has nothing to show.
   * Drives the chip's inert treatment so a non-openable chip is not a dead
   * target — it mirrors `handleFileChipClicked`'s branches exactly.
   */
  public isChipOpenable(
    document: Pick<MessageDocuments, 'extension'>,
  ): boolean {
    return (
      isImageDocumentExtension(document.extension) ||
      rendersAsPdf(document.extension)
    );
  }

  /**
   * Route an image chip to the overlay viewer, a PDF-renderable document chip to
   * the PDF viewer, and open nothing for anything else.
   *
   * Branch order is load-bearing: the image branch MUST stay first. Images have
   * no PDF rendition, so `rendersAsPdf` is false for them — putting its guard
   * ahead of the image branch would stop images opening the overlay entirely.
   */
  public handleFileChipClicked(document: MessageDocuments) {
    if (isImageDocumentExtension(document.extension)) {
      this.imageClicked.emit({
        documentUuid: document.uuid,
        filename: document.filename,
      });
      return;
    }
    if (!rendersAsPdf(document.extension)) {
      return;
    }
    this.documentClicked.emit({ documentUuid: document.uuid });
  }

  private animateMarkdownBlocks(
    root: HTMLElement,
    onComplete?: () => void,
    onSpacerUpdate?: () => void,
  ) {
    /**
     * A CSS selector string that targets the blocks that should be animated.
     *
     * `sd-response-table` is the table widget's host. It has to be listed here
     * as well as in `plainRevealTags`, and for two reasons rather than one: it
     * is how the widget becomes a revealable block at all, and it is what makes
     * the top-level filter below discard the widget's *internals* — its inner
     * `<table>`, and the `<p>` in its title bar and footer, all of which match
     * this selector and would otherwise be revealed as if they were siblings of
     * the prose.
     *
     * `squadbox-response-chart` is listed for the same reasons.
     */
    const blockSelector = `p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, code, table, hr, sd-response-table, squadbox-response-chart`;
    /**
     * An array of tag names that are explicitly marked for plain reveal.
     * These are typically elements that should not have word-by-word animation applied.
     *
     * The widget reveals as one block: splitting its words would tear a table
     * apart cell by cell, and `SplitText` on a 500-row table is exactly the
     * cost the render caps exist to avoid.
     */
    const plainRevealTags = new Set([
      'pre',
      'code',
      'table',
      'hr',
      'sd-response-table',
      // A chart reveals whole, like the table widget.
      'squadbox-response-chart',
    ]);
    /**
     * Specifies the easing function to be used for animations.
     */
    const ease: gsap.EaseString = 'power1.out';
    /**
     * A configuration property that determines whether the "Word Reveal" feature is enabled.
     * When set to true, the feature is activated.
     */
    const enableWordReveal = true;
    /**
     * The `blockOverlap` property defines the percentage value
     * to specify the Overlap from prev block sub-timeline start (e.g., '50%', '2 s')
     * 100% means no overlap
     */
    const blockOverlap = '100%';

    /**
     * Duration, in seconds, for the block reveal animation.
     * This setting applies to plain reveal blocks and blocks
     * that contain browser-generated elements (e.g., `::marker` in `<li>`).
     */
    const blockRevealDuration = 0.1;
    /**
     * Represents the duration (in seconds) for a word reveal animation.
     */
    const wordRevealDuration = 0.3;
    /**
     * Defines the duration (in seconds) for the reveal animation of quick actions.
     */
    const quickActionsRevealDuration = 0.2;
    /**
     * The duration in seconds for the reveal animation of documents.
     */
    const documentsRevealDuration = 0.2;
    /**
     * Represents the stagger delay between words in an animation or transition sequence.
     * The value is a numeric representation of time, often in seconds.
     */
    const wordStagger = 0.025;

    // Supersede whatever pass is pending or in flight before starting another.
    // The effect that calls this re-runs on every content change, so a streaming
    // reply arrives here once per chunk; without this, each call queued its own
    // builder and every resulting timeline re-hid the same blocks while the
    // previous one was still revealing them. Reverting first also means the DOM
    // this pass measures is the plain one, not a half-animated snapshot of the
    // last.
    this.cleanupResponseAnimation();

    this.markdownBlocksTimer = setTimeout(() => {
      this.markdownBlocksTimer = null;
      const allBlocks = gsap.utils.selector(root)<HTMLElement>(blockSelector);

      // Only keep top-level blocks (exclude blocks that have another block ancestor).
      // This prevents cases like <li><p>...</p></li> where <p> would be hidden
      // while we animate its words.
      const blocks = allBlocks.filter((el) => {
        let parent = el.parentElement;
        while (parent && parent !== root) {
          if (allBlocks.includes(parent)) return false; // skip nested elements
          parent = parent.parentElement;
        }
        return true; // no block ancestor found => keep
      });
      if (!blocks.length) {
        // Nothing to reveal block by block — e.g. a chart-only reply, whose
        // chart is still its `@defer` placeholder (not a block) at this
        // point. The wrapper's `opacity-0` initial-hide must still be lifted,
        // or the reply stays invisible until `isNewReply` goes false.
        gsap.set(root, { autoAlpha: 1 });
        onComplete?.();
        return;
      }

      const quickActions = this.chatbotQuickActions()?.elementRef
        .nativeElement as HTMLElement;
      const documentsContainer = this.chatbotDocumentsContainer()
        ?.nativeElement as HTMLElement;
      const master = gsap.timeline({
        defaults: { ease: ease },
        onComplete: () => onComplete?.(),
        onUpdate: () => onSpacerUpdate?.(),
      });

      // Keep everything hidden until it's that block's turn
      master.set(root, { autoAlpha: 0 });

      // Use display:none (not autoAlpha:0) so hidden blocks don't occupy layout
      // space.
      //
      // ⚠️ Applied IMPERATIVELY, before the timeline exists, and it must stay
      // that way. As a `master.set(...)` this was a timeline child scheduled at
      // time 0 — and a child can be scheduled *before* it. A word-reveal block
      // carries `sub.delay(-wordRevealDuration)`, which can drive its start
      // time negative; GSAP answers a negative start by shifting every child
      // later, which slid this hide behind that block's own
      // `clearProps: 'display'`. The block then cleared its display, the bulk
      // hide re-applied it a tick later, and nothing was left to clear it
      // again — leaving it fully faded in (`opacity: 1; visibility: inherit`)
      // and `display: none` forever.
      //
      // Measured on a `table`, `hr`, `p` response: the paragraph's sub-timeline
      // started at 0.000s and this set at 0.100s. Hiding here instead makes the
      // ordering unrepresentable rather than merely unlikely, since nothing the
      // timeline schedules can precede work already done.
      gsap.set(blocks, { display: 'none' });
      this.markdownBlocksHidden = blocks;
      if (quickActions) {
        master.set(quickActions, { autoAlpha: 0 });
      }
      if (documentsContainer) {
        master.set(documentsContainer, { autoAlpha: 0 });
      }

      // Start with the root element and reveal it
      master.to(root, { autoAlpha: 1 });
      // Then, reveal each block in turn
      blocks.forEach((block, index) => {
        // For each block, create a sub-timeline to have more control over sequencing
        const sub = gsap.timeline();
        const tag = block.tagName.toLowerCase();
        const isPlainReveal = plainRevealTags.has(tag);

        // For complex blocks or when word reveal is disabled, just reveal them
        if (isPlainReveal || !enableWordReveal) {
          sub.set(block, { clearProps: 'display', autoAlpha: 0 });
          sub.to(block, {
            autoAlpha: 1,
            duration: blockRevealDuration,
          });
        } else {
          const delay = index === 0 ? 0 : -wordRevealDuration;
          sub.delay(delay);

          const split = new SplitText(block, { type: 'words' });
          // Tracked so an interrupted pass can undo the split; the onComplete
          // below only fires when the words tween actually finishes.
          this.markdownBlocksSplits.push(split);
          const words = split.words ?? [];

          // Restore natural display (remove display:none) while keeping it invisible, then reveal words
          sub.set(block, { clearProps: 'display', autoAlpha: 0 });
          sub.set(words, { autoAlpha: 0 });
          sub.to(block, {
            autoAlpha: 1,
            duration: blockRevealDuration,
          });
          sub.to(words, {
            autoAlpha: 1,
            duration: wordRevealDuration,
            stagger: wordStagger,
            ease: ease,
            onComplete: () => split.revert(),
          });
        }

        // Add a sub timeline with overlap relative to the start of the previous one.
        // The first one starts at 0, the next ones start earlier by blockOverlap.
        const position = index === 0 ? '' : `<${blockOverlap}`;
        master.add(sub, position);
      });

      // Reveal quick actions and documents after the blocks
      if (quickActions) {
        master.to(quickActions, {
          autoAlpha: 1,
          duration: quickActionsRevealDuration,
        });
      }
      if (documentsContainer) {
        master.to(documentsContainer, {
          autoAlpha: 1,
          duration: documentsRevealDuration,
        });
      }

      this.markdownBlocksAnimation = master;
    }, 0); // to ensure the Markdown element is rendered
  }

  /**
   * Undo the reveal, rather than freeze it where it stands.
   *
   * Every step here exists because the reveal leaves the DOM in a state only it
   * knows how to undo, and an interrupted pass must not be able to strand
   * content. Ordering matters:
   *
   * 1. Cancel a builder that has not run — otherwise it hides blocks *after*
   *    this has finished putting them back.
   * 2. `revert()` the timeline, **not** `kill()`. `kill()` stops it without
   *    undoing anything it wrote, so a block mid-fade keeps whatever opacity it
   *    had. `revert()` restores what the timeline set, so an interrupted pass
   *    lands on the same DOM as a pass that never ran.
   * 3. Clear the imperative `display: none`, which is deliberately outside the
   *    timeline and so invisible to `revert()`.
   * 4. Revert the splits: a GSAP timeline knows nothing about the DOM surgery
   *    `SplitText` performed, and each split's own revert is wired to the
   *    completion of a word tween that an interrupted pass never reaches. Left
   *    alone, the per-word `inline-block` spans stay and change line breaking.
   *
   * Plain visible content is the right resting state in every case that reaches
   * here: `isNewReply` is false by then, so the wrapper's `opacity-0`
   * initial-hide is gone too.
   */
  private cleanupResponseAnimation(): void {
    if (this.markdownBlocksTimer !== null) {
      clearTimeout(this.markdownBlocksTimer);
      this.markdownBlocksTimer = null;
    }
    if (this.markdownBlocksAnimation) {
      this.markdownBlocksAnimation.revert();
      this.markdownBlocksAnimation = null;
    }
    if (this.markdownBlocksHidden.length > 0) {
      gsap.set(this.markdownBlocksHidden, { clearProps: 'display' });
      this.markdownBlocksHidden = [];
    }
    for (const split of this.markdownBlocksSplits) {
      split.revert();
    }
    this.markdownBlocksSplits = [];
  }
}
